import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Bot, Maximize, Menu, X, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import ChatInput from './ChatInput';
import MessageList from './MessageList';
import FullscreenChatDrawer from './FullscreenChatDrawer';
import SessionSidebar from './SessionSidebar';
import { Message, OrgDataChatbotProps } from './types';
import { ChatSettingsConfig } from './ChatSettings';
import { nanoid } from 'nanoid';
import { useChatSessions } from '@/hooks/useChatSessions';
import { getConnectorFriendlyName } from '@/utils/connectorNames';
import { API_BASE_URL, API_KEY } from '@/utils/apiConfig';

const OrgDataChatbot: React.FC<OrgDataChatbotProps> = ({
  organisation,
  onQueryResults
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [currentToolCall, setCurrentToolCall] = useState<string | null>(null);
  const [chatSettings, setChatSettings] = useState<ChatSettingsConfig>({
    model: 'gpt-5-mini-2025-08-07',
    maxTokens: 10000,
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { sessions, createSession, isLoading: isSessionsLoading } = useChatSessions(organisation?.id);
  const isMobile = useIsMobile();

  // Initialize or load session
  useEffect(() => {
    const initializeChat = async () => {
      if (!organisation?.id) {
        setIsLoadingHistory(false);
        setMessages([createWelcomeMessage()]);
        return;
      }

      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (userError) {
          console.error('Error getting user:', userError);
          setMessages([createWelcomeMessage()]);
          setIsLoadingHistory(false);
          return;
        }

        if (user) {
          const existingSessions = sessions.filter(s => s.organisation_id === organisation.id);

          // Check if current session still exists
          const currentSessionExists = currentSessionId && existingSessions.some(s => s.session_id === currentSessionId);

          if (existingSessions.length > 0) {
            // Only auto-select if we don't have a current session or if the current session was deleted
            if (!hasActiveSession || !currentSessionExists) {
              const mostRecentSession = existingSessions[0];
              setCurrentSessionId(mostRecentSession.session_id);
              setHasActiveSession(true);
              await loadChatHistory(mostRecentSession.session_id);
            }
            // If we have an active session that still exists, don't change it
          } else {
            // Only create a new session if we have loaded sessions and found none
            // This prevents creating sessions on every reload
            if (!isSessionsLoading) {
              const newSessionId = await createSession('New Chat', organisation.id);
              if (newSessionId) {
                setCurrentSessionId(newSessionId);
                setHasActiveSession(true);
                setMessages([createWelcomeMessage()]);
              } else {
                setMessages([createWelcomeMessage()]);
                setHasActiveSession(false);
              }
            } else {
              // Still loading sessions, just show welcome message
              setMessages([createWelcomeMessage()]);
              setHasActiveSession(false);
            }
          }
        } else {
          setMessages([createWelcomeMessage()]);
          setHasActiveSession(false);
        }
      } catch (error) {
        console.error('Error initializing chat:', error);
        setMessages([createWelcomeMessage()]);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    // Only initialize when sessions have finished loading
    if (!isSessionsLoading) {
      initializeChat();
    }
  }, [organisation?.id, sessions, createSession, isSessionsLoading]);

  const createWelcomeMessage = (): Message => ({
    id: '1',
    text: `Hello! I'm your intelligent assistant for ${organisation?.name || 'your organization'}. I can help you find orders, manage customers, analyze data, and much more by querying your connected databases.`,
    sender: 'bot',
    timestamp: Date.now(),
  });

  // Parse export information from chat response text
  const parseExportInfo = (responseText: string) => {
    try {
      // Look for export ID patterns in the response
      const exportIdMatch = responseText.match(/"export_id":\s*"([^"]+)"/);
      const statusMatch = responseText.match(/"status":\s*"([^"]+)"/);
      const tableNameMatch = responseText.match(/"table_name":\s*"([^"]+)"/);
      const schemaNameMatch = responseText.match(/"schema_name":\s*"([^"]+)"/);
      const connectionIdMatch = responseText.match(/"connection_id":\s*"([^"]+)"/);

      if (exportIdMatch && statusMatch && tableNameMatch) {
        return {
          export_id: exportIdMatch[1],
          status: statusMatch[1] as 'queued' | 'processing' | 'completed' | 'failed',
          table_name: tableNameMatch[1],
          schema_name: schemaNameMatch?.[1] || 'public',
          connection_id: connectionIdMatch?.[1] || 'unknown'
        };
      }

      // Also check for export success messages in plain text
      const exportSuccessMatch = responseText.match(/Export started successfully.*?export[_\s]?id[:\s]*([a-zA-Z0-9_-]+)/i);
      const tableMatch = responseText.match(/table\s+([a-zA-Z0-9_]+)/i);

      if (exportSuccessMatch && tableMatch) {
        return {
          export_id: exportSuccessMatch[1],
          status: 'queued' as const,
          table_name: tableMatch[1],
          schema_name: 'public',
          connection_id: 'unknown'
        };
      }
    } catch (error) {
      console.error('Error parsing export info:', error);
    }
    return null;
  };

  const loadChatHistory = async (sessionId: string) => {
    try {

      const { data: messagesData, error: messagesError } = await supabase
        .from('chat_messages')
        .select('id, role, content, content_old, tool_calls, created_at, message_order, input_tokens, output_tokens, reasoning_tokens, total_cost, model')
        .eq('session_id', sessionId)
        .order('message_order', { ascending: true });

      if (messagesError) {
        console.error('Error loading messages:', messagesError);
        setMessages([createWelcomeMessage()]);
        return;
      }

      if (messagesData && messagesData.length > 0) {

        const loadedMessages: Message[] = messagesData.map((msg) => {
          // More robust exportInfo extraction
          let exportInfo = undefined;
          let toolCallsData = null;
          if (msg.tool_calls) {
            try {
              // Handle different possible formats of tool_calls JSONB data
              toolCallsData = msg.tool_calls;

              // If it's a string, try to parse it
              if (typeof toolCallsData === 'string') {
                toolCallsData = JSON.parse(toolCallsData);
              }

              // Check if exportInfo exists in the tool_calls data
              if (toolCallsData && typeof toolCallsData === 'object') {
                if ('exportInfo' in toolCallsData) {
                  exportInfo = toolCallsData.exportInfo;
                }
              }
            } catch (parseError) {
              console.warn('[DEBUG] Failed to parse tool_calls for exportInfo:', parseError, msg.tool_calls);
            }
          }

          // Extract message text - support both new JSONB format and old text format
          let messageText = '';
          if (msg.content && Array.isArray(msg.content)) {
            // New JSONB format: array of content parts
            if (msg.content.length > 0) {
              messageText = msg.content
                .map((item: any) => item?.text || '')
                .filter(Boolean)
                .join('\n');
            }
          } else if (msg.content && typeof msg.content === 'object') {
            // Handle object format
            messageText = JSON.stringify(msg.content);
          } else if (msg.content) {
            // Plain text in new content column
            messageText = String(msg.content);
          }

          // FINAL fallback to old preserved column when new content is empty
          if ((!messageText || messageText.trim() === '') && (msg as any).content_old) {
            messageText = String((msg as any).content_old);
          }

          // Also try to parse from content as fallback
          if (!exportInfo) {
            exportInfo = parseExportInfo(messageText);
          }

          return {
            id: msg.id,
            text: messageText,
            sender: msg.role === 'user' ? 'user' : 'bot',
            timestamp: new Date(msg.created_at).getTime(),
            metadata: {
              sessionId: sessionId,
              isStreaming: false,
              exportInfo: exportInfo,
              multiPlatform: toolCallsData?.multiPlatform || false,
              input_tokens: msg.input_tokens || undefined,
              output_tokens: msg.output_tokens || undefined,
              reasoning_tokens: msg.reasoning_tokens || undefined,
              total_cost: msg.total_cost ? Number(msg.total_cost) : undefined,
              model: msg.model || undefined
            }
          };
        });

        setMessages(loadedMessages);
      } else {
        setMessages([createWelcomeMessage()]);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      setMessages([createWelcomeMessage()]);
    }
  };

  const handleSessionSelect = useCallback(async (sessionId: string) => {
    if (!sessionId) {
      // No session selected, clear everything
      setCurrentSessionId('');
      setHasActiveSession(false);
      setMessages([createWelcomeMessage()]);
      setIsMobileSidebarOpen(false);
      return;
    }

    setCurrentSessionId(sessionId);
    setHasActiveSession(true);
    await loadChatHistory(sessionId);
    setIsMobileSidebarOpen(false);
  }, []);

  const handleNewSession = useCallback(async (sessionId: string) => {
    setCurrentSessionId(sessionId);
    setHasActiveSession(true);
    setMessages([createWelcomeMessage()]);
    setIsMobileSidebarOpen(false);
  }, [organisation]);

  const ensureActiveSession = async (): Promise<string> => {
    if (hasActiveSession && currentSessionId) {
      return currentSessionId;
    }

    const { data: { user } } = await supabase.auth.getUser();

    if (user) {
      const newSessionId = await createSession('New Chat', organisation?.id);
      if (newSessionId) {
        setCurrentSessionId(newSessionId);
        setHasActiveSession(true);
        return newSessionId;
      }
    } else {
      const tempSessionId = nanoid();
      setCurrentSessionId(tempSessionId);
      setHasActiveSession(true);
      return tempSessionId;
    }

    throw new Error('Failed to create session');
  };

  // Process export info markers from accumulated buffer
  const processExportMarkers = (buffer: string): { cleanText: string; exportInfo: any } => {
    let cleanText = buffer;
    let exportInfo = null;

    // Look for complete export markers in the buffer
    const exportInfoMatch = buffer.match(/__EXPORT_INFO__(.+?)__END_EXPORT_INFO__/);
    if (exportInfoMatch) {
      try {
        exportInfo = JSON.parse(exportInfoMatch[1]);
        // Remove the marker from the text
        cleanText = buffer.replace(/__EXPORT_INFO__.+?__END_EXPORT_INFO__/g, '');
      } catch (parseError) {
        console.warn('[MARKER] Failed to parse export info:', parseError);
        // Still remove the marker even if parsing fails
        cleanText = buffer.replace(/__EXPORT_INFO__.+?__END_EXPORT_INFO__/g, '');
      }
    }

    return { cleanText: cleanText.trim(), exportInfo };
  };

  const handleSendMessage = useCallback(async (text: string, settings: ChatSettingsConfig) => {
    if (!text.trim()) return;

    try {
      const sessionId = await ensureActiveSession();

      const userMessage: Message = {
        id: nanoid(),
        text: text.trim(),
        sender: 'user',
        timestamp: Date.now()
      };

      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);

      const botMessage: Message = {
        id: nanoid(),
        text: '',
        sender: 'bot',
        timestamp: Date.now(),
        metadata: {
          sessionId: sessionId,
          isStreaming: true
        }
      };

      setMessages(prev => [...prev, botMessage]);

      // Set immediate "Thinking..." indicator
      setCurrentToolCall('Thinking...');

      const orgContext = organisation && organisation.id ? {
        id: organisation.id,
        name: organisation.name || 'Unknown Organization',
        connections: organisation.connections || []
      } : null;

      if (!orgContext) {
        throw new Error('Organization context is required for chat functionality');
      }

      // Get current session for authentication
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(`${API_BASE_URL}/mcp-chat-v3`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
          'apikey': API_KEY
        },
        body: JSON.stringify({
          text: text.trim(),
          query: text.trim(),
          session_id: sessionId,
          organisation_id: organisation.id,
          organisation_context: orgContext,
          model: settings.model,
          max_tokens: settings.maxTokens,
          reasoning_effort: settings.reasoningEffort
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // SSE streaming handler - properly parse Server-Sent Events
      const handleStream = async (stream: ReadableStream) => {
        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let assistantText = '';
        let streamedExportInfo = null;
        let actualTokenUsage: { input_tokens: number; output_tokens: number; reasoning_tokens: number; total_cost: number; model?: string } | null = null;

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            // Decode chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });

            // Process complete SSE events (separated by \n\n or \n)
            let boundary: number;
            while ((boundary = buffer.indexOf('\n')) !== -1) {
              const line = buffer.slice(0, boundary).trim();
              buffer = buffer.slice(boundary + 1);

              // Skip empty lines and non-data lines
              if (!line || !line.startsWith('data:')) continue;

              // Extract JSON after "data: " prefix (note: "data: " is 6 chars with space)
              const jsonStr = line.slice(6).trim();
              if (jsonStr === '[DONE]') continue;

              try {
                const event = JSON.parse(jsonStr);

                // Handle text deltas - ONLY append the text, never the raw JSON
                if (event.type === 'text_delta' && event.text) {
                  assistantText += event.text;

                  // Process export markers and update display with clean text only
                  const { cleanText, exportInfo } = processExportMarkers(assistantText);
                  if (exportInfo) {
                    streamedExportInfo = exportInfo;
                  }

                  // Update message with ONLY the clean text (no raw JSON)
                  setMessages(prev => prev.map(msg =>
                    msg.id === botMessage.id
                      ? {
                        ...msg,
                        text: cleanText,
                        metadata: {
                          ...msg.metadata,
                          isStreaming: true,
                          exportInfo: streamedExportInfo || msg.metadata?.exportInfo
                        }
                      }
                      : msg
                  ));
                }
                // Handle content events (used by metric intent path)
                else if (event.type === 'content' && event.delta?.text) {
                  assistantText += event.delta.text;

                  // Process export markers and update display with clean text only
                  const { cleanText, exportInfo } = processExportMarkers(assistantText);
                  if (exportInfo) {
                    streamedExportInfo = exportInfo;
                  }

                  // Update message with ONLY the clean text (no raw JSON)
                  setMessages(prev => prev.map(msg =>
                    msg.id === botMessage.id
                      ? {
                        ...msg,
                        text: cleanText,
                        metadata: {
                          ...msg.metadata,
                          isStreaming: true,
                          exportInfo: streamedExportInfo || msg.metadata?.exportInfo
                        }
                      }
                      : msg
                  ));
                }
                else if (event.type === 'usage' && event.input_tokens !== undefined) {
                  // Capture actual token usage from backend
                  actualTokenUsage = {
                    input_tokens: event.input_tokens,
                    output_tokens: event.output_tokens,
                    reasoning_tokens: event.reasoning_tokens || 0,
                    total_cost: event.total_cost,
                    model: event.model
                  };

                  console.log('[USAGE] Actual token usage received:', actualTokenUsage);

                  // Update bot message with token usage
                  setMessages(prev => prev.map(msg =>
                    msg.id === botMessage.id
                      ? {
                        ...msg,
                        metadata: {
                          ...msg.metadata,
                          input_tokens: actualTokenUsage.input_tokens,
                          output_tokens: actualTokenUsage.output_tokens,
                          reasoning_tokens: actualTokenUsage.reasoning_tokens,
                          total_cost: actualTokenUsage.total_cost,
                          model: actualTokenUsage.model
                        }
                      }
                      : msg
                  ));

                  // Show actual cost to user via toast
                  toast({
                    title: "Cost Information",
                    description: `This query used ${actualTokenUsage.input_tokens.toLocaleString()} input tokens and ${actualTokenUsage.output_tokens.toLocaleString()} output tokens. Total cost: $${actualTokenUsage.total_cost.toFixed(4)}`,
                    duration: 5000,
                  });
                }
                else if (event.type === 'done' || event.type === 'text_complete') {
                  console.log('[SSE] Stream completed');
                  setCurrentToolCall(null);
                }
                else if (event.type === 'tool_call') {
                  const toolName = event.name;
                  const connectionType = event.connection_type;

                  console.log('[SSE] Tool call:', toolName, connectionType ? `(${connectionType})` : '');

                  // Show specific database name if available
                  if (toolName === 'switch_database' && connectionType) {
                    const friendlyName = getConnectorFriendlyName(connectionType);
                    setCurrentToolCall(`Switching to ${friendlyName} database...`);
                  } else if (toolName === 'execute_sql' && connectionType) {
                    const friendlyName = getConnectorFriendlyName(connectionType);
                    setCurrentToolCall(`Querying ${friendlyName} database...`);
                  } else if (toolName === 'list_schemas' || toolName === 'list_objects') {
                    if (connectionType) {
                      const friendlyName = getConnectorFriendlyName(connectionType);
                      setCurrentToolCall(`Analyzing ${friendlyName} schema...`);
                    } else {
                      setCurrentToolCall('Analyzing schema...');
                    }
                  } else if (toolName === 'switch_database') {
                    setCurrentToolCall('Switching database...');
                  } else if (toolName === 'execute_sql') {
                    setCurrentToolCall('Querying database...');
                  } else {
                    setCurrentToolCall(`Executing ${toolName}...`);
                  }
                }
                else if (event.type === 'error') {
                  console.error('[SSE] Stream error:', event.error);
                  toast({
                    title: 'Error',
                    description: event.error || 'An error occurred',
                    variant: 'destructive',
                  });
                }
              } catch (parseError) {
                // Only log if it looks like it should be JSON (not empty or malformed)
                if (jsonStr.length > 0) {
                  console.warn('[SSE] Parse error:', jsonStr.slice(0, 50));
                }
              }
            }
          }
        } finally {
          // Final cleanup - ensure clean text
          setCurrentToolCall(null);
          const finalText = assistantText || 'I processed your request.';
          const { cleanText: finalCleanText, exportInfo: finalExportInfo } = processExportMarkers(finalText);
          const exportInfo = streamedExportInfo || finalExportInfo || parseExportInfo(finalText);

          setMessages(prev => prev.map(msg =>
            msg.id === botMessage.id
              ? {
                ...msg,
                text: finalCleanText,
                metadata: {
                  ...msg.metadata,
                  isStreaming: false,
                  exportInfo: exportInfo || undefined
                }
              }
              : msg
          ));
        }
      };

      // Always prefer streaming if body exists
      if (response.body) {
        await handleStream(response.body);
      } else {
        // Fallback for non-streaming responses (should not contain raw SSE)
        const responseText = await response.text();
        const { cleanText, exportInfo } = processExportMarkers(responseText);

        setMessages(prev => prev.map(msg =>
          msg.id === botMessage.id
            ? {
              ...msg,
              text: cleanText || responseText,
              metadata: {
                ...msg.metadata,
                isStreaming: false,
                exportInfo: exportInfo || parseExportInfo(responseText)
              }
            }
            : msg
        ));
      }

    } catch (error: any) {
      console.error('[handleSendMessage] Error:', error);

      setMessages(prev =>
        prev.map(msg =>
          msg.sender === 'bot' && msg.text === ''
            ? {
              ...msg,
              text: `I encountered an error: ${error.message || 'Unknown error occurred'}. Please try again.`,
              metadata: {
                ...msg.metadata,
                isStreaming: false,
                error: error.message
              }
            }
            : msg
        )
      );

      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [organisation, toast, onQueryResults, hasActiveSession, currentSessionId, createSession]);

  // Get current session name
  const currentSession = sessions.find(s => s.session_id === currentSessionId);
  const currentSessionName = hasActiveSession && currentSession?.session_name || 'No active session';

  if (isLoadingHistory || isSessionsLoading) {
    return (
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-blue-500" />
            <CardTitle>Loading your conversation...</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <div className="animate-pulse text-gray-500">Loading chat history...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="flex h-[600px] mb-6 border rounded-lg overflow-hidden bg-white">
        {/* Desktop Sidebar */}
        {!isMobile && (
          <SessionSidebar
            organisationId={organisation?.id}
            currentSessionId={currentSessionId}
            onSessionSelect={handleSessionSelect}
            onNewSession={handleNewSession}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          />
        )}

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col relative min-w-0 overflow-hidden">
          {/* Header */}
          <div className="border-b p-4 bg-white flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Mobile Menu Button - inside header */}
                {isMobile && (
                  <Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
                    <SheetTrigger asChild>
                      <Button
                        data-testid="mobile-menu-button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 mr-2"
                      >
                        <Menu className="h-4 w-4" />
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="p-0 w-[85vw] max-w-80 flex flex-col h-full">
                      <SheetTitle className="sr-only">Chat Sessions</SheetTitle>
                      <SheetDescription className="sr-only">
                        Select or manage your chat sessions
                      </SheetDescription>

                      {/* Fixed header with New button - always visible */}
                      <div className="flex-shrink-0 p-4 border-b bg-white">
                        <div className="flex items-center justify-between pr-8">
                          <h2 className="font-semibold">Chat Sessions</h2>
                          <Button
                            data-testid="mobile-new-session"
                            size="sm"
                            onClick={async () => {
                              const sessionId = await createSession('New Chat', organisation?.id);
                              if (sessionId) {
                                handleNewSession(sessionId);
                                setIsMobileSidebarOpen(false);
                              }
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            New
                          </Button>
                        </div>
                      </div>

                      {/* Session sidebar without its own header */}
                      <div className="flex-1 min-h-0 overflow-hidden">
                        <SessionSidebar
                          organisationId={organisation?.id}
                          currentSessionId={currentSessionId}
                          onSessionSelect={(sessionId) => {
                            handleSessionSelect(sessionId);
                            setIsMobileSidebarOpen(false);
                          }}
                          onNewSession={(sessionId) => {
                            handleNewSession(sessionId);
                            setIsMobileSidebarOpen(false);
                          }}
                          hideHeader={true}
                        />
                      </div>
                    </SheetContent>
                  </Sheet>
                )}
                <Bot className="h-5 w-5 text-blue-500" />
                <div>
                  <h1 className="font-semibold">EVE's Chatbot</h1>
                  <p className="text-sm text-gray-600">
                    Current session: {currentSessionName}
                  </p>
                </div>
              </div>
              <Button
                data-testid="fullscreen-chat"
                variant="ghost"
                size="sm"
                onClick={() => setIsFullscreenOpen(true)}
                className="text-blue-500 hover:text-blue-700"
              >
                <Maximize className="h-4 w-4" />
                Fullscreen
              </Button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Ask questions about your products, customers, and orders. Try queries like "Show me products with no inventory",
              "Find products with highest profit margins", or "Export the orders table to CSV". I can analyze data and export results for you.
            </p>
          </div>

          {/* Chat Content - Fixed layout with proper height constraints */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Tool Execution Status */}
            {currentToolCall && (
              <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 flex items-center gap-2 text-sm text-blue-700">
                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                <span className="font-medium">{currentToolCall}</span>
              </div>
            )}

            <div className="flex-1 min-h-0 overflow-hidden">
              <MessageList
                messages={messages}
                messagesEndRef={messagesEndRef}
                isLoadingHistory={isLoadingHistory}
                currentSessionId={currentSessionId}
              />
            </div>

            <div className="border-t p-4 flex-shrink-0 bg-white">
              <ChatInput
                isLoading={isLoading}
                onSendMessage={handleSendMessage}
                settings={chatSettings}
                onSettingsChange={setChatSettings}
              />
            </div>
          </div>
        </div>
      </div>

      <FullscreenChatDrawer
        isOpen={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
        messages={messages}
        isLoading={isLoading}
        onSendMessage={handleSendMessage}
        onNewSession={() => handleNewSession(currentSessionId)}
        onSessionSelect={handleSessionSelect}
        organisation={organisation ? {
          id: organisation.id || '',
          name: organisation.name || 'Unknown Organization',
          connections: organisation.connections || []
        } : undefined}
        currentSessionId={currentSessionId}
        isLoadingHistory={isLoadingHistory}
        currentToolCall={currentToolCall}
        settings={chatSettings}
        onSettingsChange={setChatSettings}
      />
    </>
  );
};

export default OrgDataChatbot;
